import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Alert,
    Typography, IconButton, TextField, Divider, Chip,
} from '@mui/material';
import {
    Close as CloseIcon, Stop as StopIcon,
    Search as SearchIcon, QrCodeScanner as ScanIcon,
} from '@mui/icons-material';
import { Html5Qrcode } from 'html5-qrcode';
import { extractQrToken } from '../../utils/qrToken';

const scannerDivId = 'qr-scanner-modal-region';

/**
 * Reusable QR scanner in a modal.
 *
 * Props:
 *   open         — bool
 *   onClose      — () => void
 *   onDetected   — (token: string) => void   Called with the extracted 32-hex token.
 *   title        — string                    Modal title. Default: "Scan QR code".
 *   hint         — string                    Small help text under the video.
 *
 * Accepts both URL form (…/t/TOKEN) and raw 32-hex token.
 */
export default function QrScannerModal({
    open,
    onClose,
    onDetected,
    title = 'Scan QR code',
    hint = 'Point the camera at the QR code on the item tag.',
}) {
    const scannerRef = useRef(null);
    const [error, setError] = useState(null);
    const [manualToken, setManualToken] = useState('');

    const stopScanner = useCallback(async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop();
                scannerRef.current.clear();
            } catch (e) { /* ignore */ }
            scannerRef.current = null;
        }
    }, []);

    const startScanner = useCallback(async () => {
        setError(null);
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setError('Camera is not available on this device. Enter the token manually.');
                return;
            }
            // Ask for permission — triggers the browser prompt if not yet decided
            const tempStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            tempStream.getTracks().forEach(t => t.stop());

            if (!document.getElementById(scannerDivId)) return; // modal closing

            const scanner = new Html5Qrcode(scannerDivId);
            scannerRef.current = scanner;

            await scanner.start(
                { facingMode: 'environment' },
                { fps: 30, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0, disableFlip: false },
                (decodedText) => {
                    const token = extractQrToken(decodedText);
                    if (token) {
                        stopScanner();
                        onDetected(token);
                    } else {
                        setError('This QR code is not recognised.');
                    }
                },
                () => {} // ignore per-frame decode errors
            );
        } catch (e) {
            if (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError') {
                setError('Camera access denied. Enable camera for this site or enter the token manually.');
            } else if (e?.name === 'NotFoundError' || e?.name === 'DevicesNotFoundError') {
                setError('No camera found. Enter the token manually.');
            } else if (e?.name === 'NotReadableError') {
                setError('Camera is already in use. Close the other app and try again.');
            } else {
                setError('Failed to start camera.');
            }
        }
    }, [onDetected, stopScanner]);

    useEffect(() => {
        if (open) {
            const t = setTimeout(() => { startScanner(); }, 80);
            return () => { clearTimeout(t); stopScanner(); };
        }
        return undefined;
    }, [open, startScanner, stopScanner]);

    const handleClose = async () => {
        await stopScanner();
        setError(null);
        setManualToken('');
        onClose();
    };

    const handleManualSubmit = () => {
        const token = extractQrToken(manualToken);
        if (!token) {
            setError('Paste the full URL (…/t/TOKEN) or the 32-character token.');
            return;
        }
        stopScanner();
        setManualToken('');
        onDetected(token);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                <ScanIcon color="primary" />
                {title}
                <IconButton onClick={handleClose} sx={{ ml: 'auto' }} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{hint}</Typography>

                <Box
                    id={scannerDivId}
                    sx={{
                        width: '100%', maxWidth: 380, mx: 'auto', borderRadius: 2, overflow: 'hidden',
                        mb: 2, border: '2px solid #1976d2', position: 'relative', minHeight: 220,
                        '& video': { borderRadius: 1 },
                        '@keyframes scanline': {
                            '0%':   { top: '10%' },
                            '50%':  { top: '85%' },
                            '100%': { top: '10%' },
                        },
                        '&::after': {
                            content: '""', position: 'absolute', left: '15%', right: '15%',
                            height: '2px', backgroundColor: '#4caf50',
                            boxShadow: '0 0 8px 2px rgba(76,175,80,0.5)',
                            animation: 'scanline 2.5s ease-in-out infinite',
                            zIndex: 10, pointerEvents: 'none',
                        },
                    }}
                />

                <Divider sx={{ my: 2 }}><Chip label="OR" size="small" /></Divider>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Enter token manually</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        size="small" fullWidth
                        placeholder="Paste URL or 32-char token…"
                        value={manualToken}
                        onChange={e => setManualToken(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleManualSubmit(); }}
                    />
                    <Button variant="contained" onClick={handleManualSubmit} disabled={!manualToken.trim()} sx={{ flexShrink: 0 }}>
                        <SearchIcon />
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} startIcon={<StopIcon />}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
